import React, { useState } from 'react';
import { Check, X, Star, ArrowRight, Zap, Shield, Eye, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';

const LandingPricing: React.FC = () => {
    const [billingCycle] = useState<'monthly' | 'yearly'>('monthly');

    const plans = [
        {
            name: 'Notifier Plan',
            description: 'For SMBs and Individuals',
            price: { monthly: 'Contact us', yearly: 'Contact us' },
            yearlyDiscount: false,
            popular: true,
            features: {
                included: [
                    '2 domains (Per Owner Verification)',
                    'Unmasked data (50 unlocks per month)',
                    'coperate records, Third Party Records, Email',
                    '1 seat',
                    '20 requests per day'
                ],
                excluded: [
                    'Real time Alerting',
                    'Saving Incidents',
                    'Threat Profiling',
                    'Multiple seats'
                ]
            },
            cta: 'Get a Quote',
            gradient: 'from-blue-500 to-cyan-500',
            icon: Eye
        },
        {
            name: 'Operator Plan',
            description: 'For MSSPs and SOCs',
            price: { monthly: 'Contact us', yearly: 'Contact us' },
            yearlyDiscount: false,
            popular: false,
            features: {
                included: [
                    '50 Domains (Per Owner Verification)',
                    'Unmasked Data (200 unlocks per month)',
                    'coperate records, Third Party Records, Email',
                    'Real time Alerting',
                    'Saving Incidents',
                    '3 seats',
                    '200 requests per day'
                ],
                excluded: [
                    'Threat Profiling',
                    'Threat Feeds',
                    'System Information',
                    'IP addresses'
                ]
            },
            cta: 'Get a Quote',
            gradient: 'from-purple-500 to-pink-500',
            icon: Shield
        },
        {
            name: 'Informant Plan',
            description: 'For Large Enterrprise and Governments',
            price: { monthly: 'Contact us', yearly: 'Contact us' },
            yearlyDiscount: false,
            popular: false,
            features: {
                included: [
                    'Unlimitted Domains',
                    'Fully Unmasked Data',
                    'Coperate records, Third Party Records, Email, System Information, IP addresses',
                    'Real time Alerting',
                    'Saving Incidents',
                    'Threat Profiling',
                    'Threat Feeds',
                    '6 seats',
                    '300 requests per day'
                ],
                excluded: []
            },
            cta: 'Get a Quote',
            gradient: 'from-green-500 to-emerald-500',
            icon: Globe
        }
    ];

    const faqs = [
        {
            question: 'What counts as a data unlock?',
            answer: 'A data unlock allows you to view detailed information about a specific credential or breach record. This includes usernames, passwords, device information, and other metadata.'
        },
        {
            question: 'Can I upgrade or downgrade my plan?',
            answer: 'Yes, you can change your plan at any time. Upgrades take effect immediately, while downgrades take effect at the next billing cycle.'
        },
        {
            question: 'Do you offer custom enterprise solutions?',
            answer: 'Absolutely. We work with large enterprises to create custom solutions that meet their specific threat intelligence needs. Contact our sales team for details.'
        },
        {
            question: 'What payment methods do you accept?',
            answer: 'We accept all major credit cards, bank transfers, and can accommodate purchase orders for enterprise customers.'
        },
        {
            question: 'Is there a free trial available?',
            answer: 'Yes, our Freemium plan allows you to explore the platform with limited access. You can also contact us for a demo of our paid features.'
        }
    ];

    const getPrice = (plan: typeof plans[0]) => {
        if (typeof plan.price.monthly === 'string') return plan.price.monthly;
        const price = billingCycle === 'yearly' ? plan.price.yearly : plan.price.monthly;
        return price;
    };

    const getSavingsText = (plan: typeof plans[0]) => {
        if (typeof plan.price.monthly === 'string' || typeof plan.price.yearly === 'string' || !plan.yearlyDiscount || billingCycle === 'monthly') return '';
        const monthlyCost = plan.price.monthly * 12;
        const yearlyCost = plan.price.yearly * 12;
        const savings = Math.round(((monthlyCost - yearlyCost) / monthlyCost) * 100);
        return `Save ${savings}%`;
    };

    return (
        <div className="min-h-screen bg-background-light dark:bg-background-dark">
            {/* Hero Section */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center px-4 py-2 bg-primary-500/10 border border-primary-500/20 rounded-full mb-6">
                        <Zap className="h-4 w-4 text-primary-500 mr-2" />
                        <span className="text-sm font-medium text-primary-500">
                            Simple, Transparent Pricing
                        </span>
                    </div>

                    <h1 className="text-[1.75rem] sm:text-3xl md:text-6xl leading-tight font-grotesk font-bold text-text-primary-light dark:text-text-primary-dark mb-6 mx-4 md:mx-0 break-words text-balance">
                        
                        <span className="block sm:inline bg-gradient-to-r from-gradient-start to-gradient-end bg-clip-text text-transparent">
                            workflow
                        </span>
                    </h1>

                    <p className="text-xl text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto mb-12">
                        Whether you're exploring leaks, conducting offensive security, or integrating threat intel into your stack.
                    </p>
                </div>
            </section>

            {/* Pricing Cards */}
            <section className="py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-3 gap-8">
                        {plans.map((plan, index) => (
                            <div
                                key={index}
                                className={`
                                    relative p-8 rounded-xl border-2 transition-all duration-300 hover:shadow-xl hover:transform hover:-translate-y-1
                                    ${plan.popular
                                    ? 'border-primary-500 bg-background-light dark:bg-background-dark-elevated'
                                    : 'border-border-light dark:border-border-dark-subtle bg-background-light-elevated dark:bg-background-dark-elevated hover:border-primary-500/50'
                                }
                                `}
                            >
                                {plan.popular && (
                                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                        <div className="bg-gradient-to-r from-gradient-start to-gradient-end text-white px-4 py-1 rounded-full text-sm font-medium flex items-center">
                                            <Star className="h-4 w-4 mr-1" />
                                            Most Popular
                                        </div>
                                    </div>
                                )}

                                {/* Plan Header */}
                                <div className="text-center mb-8">
                                    <div className={`w-16 h-16 bg-gradient-to-r ${plan.gradient} rounded-full flex items-center justify-center mx-auto mb-4`}>
                                        <plan.icon className="h-8 w-8 text-white" />
                                    </div>

                                    <h3 className="text-2xl font-grotesk font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
                                        {plan.name}
                                    </h3>

                                    <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm mb-4">
                                        {plan.description}
                                    </p>

                                    {/* Price */}
                                    <div className="mb-2">
                                        <span className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                                            {getPrice(plan)}
                                        </span>
                                    </div>

                                    {billingCycle === 'yearly' && plan.yearlyDiscount && typeof plan.price.monthly === 'number' && (
                                        <div className="text-sm text-primary-500 font-medium">
                                            {getSavingsText(plan)}
                                        </div>
                                    )}
                                </div>

                                {/* Features */}
                                <div className="space-y-4 mb-8">
                                    {plan.features.included.map((feature, featureIndex) => (
                                        <div key={featureIndex} className="flex items-start space-x-3">
                                            <Check className="h-5 w-5 text-success-500 mt-0.5 flex-shrink-0" />
                                            <span className="text-text-secondary-light dark:text-text-secondary-dark text-sm">
                                                {feature}
                                            </span>
                                        </div>
                                    ))}

                                    {plan.features.excluded.map((feature, featureIndex) => (
                                        <div key={featureIndex} className="flex items-start space-x-3 opacity-50">
                                            <X className="h-5 w-5 text-text-muted-light dark:text-text-muted-dark mt-0.5 flex-shrink-0" />
                                            <span className="text-text-muted-light dark:text-text-muted-dark text-sm">
                                                {feature}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {/* CTA Button */}
                                <Link
                                    to="/contact"
                                    className={`
                                        w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 text-center block
                                        ${plan.popular
                                        ? 'bg-gradient-to-r from-gradient-start to-gradient-end text-white hover:shadow-lg transform hover:scale-105'
                                        : 'bg-background-light dark:bg-background-dark-secondary border border-border-light dark:border-border-dark-subtle text-text-primary-light dark:text-text-primary-dark hover:border-primary-500'
                                    }
                                    `}
                                >
                                    {plan.cta}
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-16 bg-background-light-secondary dark:bg-background-dark-secondary">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-grotesk font-bold text-text-primary-light dark:text-text-primary-dark mb-4">
                            Frequently Asked Questions
                        </h2>
                        <p className="text-text-secondary-light dark:text-text-secondary-dark">
                            Everything you need to know about our pricing and plans
                        </p>
                    </div>

                    <div className="space-y-6">
                        {faqs.map((faq, index) => (
                            <div
                                key={index}
                                className="bg-background-light dark:bg-background-dark-elevated border border-border-light dark:border-border-dark-subtle rounded-lg p-6"
                            >
                                <h3 className="text-lg font-grotesk font-semibold text-text-primary-light dark:text-text-primary-dark mb-3">
                                    {faq.question}
                                </h3>
                                <p className="text-text-secondary-light dark:text-text-secondary-dark">
                                    {faq.answer}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center space-x-6 p-8 bg-background-light-elevated dark:bg-background-dark-elevated border border-border-light dark:border-border-dark-subtle rounded-xl">
                        <div className="text-left">
                            <h3 className="text-xl font-grotesk font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
                                Ready to Explore?
                            </h3>
                            <p className="text-text-secondary-light dark:text-text-secondary-dark">
                                Detect exposed credentials in seconds using our powerful search engine
                            </p>
                        </div>
                        <Link
                            to="/app"
                            className="flex items-center px-8 py-4 text-lg font-medium text-white bg-gradient-to-r from-gradient-start to-gradient-end rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200 whitespace-nowrap"
                        >
                            Get a Free Account
                            <ArrowRight className="ml-2 h-5 w-5" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default LandingPricing;